
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { type NodeProps, useNodesData, useNodeConnections } from '@xyflow/react'
import type { GenieNodeState } from '@genieartinc/schemas'
import { MaskNode } from '@/components/canvas/nodes'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import type { HandleMeta } from '@/lib/canvas/types'
import { PURPOSE_HANDLES } from '@/lib/canvas/configs/handles'

export interface MaskNodeNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

/** Default handles from centralized config */
const MASK_HANDLES: HandleMeta[] = PURPOSE_HANDLES.MaskNode.default

function MaskNodeNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as MaskNodeNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  // Resolve connected image from upstream nodes
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(() => incomingConnections.map(c => c.source), [incomingConnections])
  const sourceNodesData = useNodesData(sourceNodeIds)

  const connectedImage = useMemo(() => {
    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      const sourceData = nodeEntry.data as Record<string, unknown>
      const sourceState = sourceData?.state as Record<string, unknown> | undefined
      if (!sourceState) continue

      const sourceType = nodeEntry.type || ''
      if (sourceType === 'ImageFile') {
        const fileMeta = sourceState.fileMetadata as Record<string, unknown> | undefined
        if (fileMeta?.publicUrl) return fileMeta.publicUrl as string
      }
      if (sourceType === 'ImageNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const lastImage = createdItems?.filter(i => i.type === 'image').pop()
        if (lastImage?.content) return lastImage.content
      }
      const images = sourceState.images as string[] | undefined
      if (images && images.length > 0) return images[images.length - 1]
    }
    return null
  }, [sourceNodesData])

  const imageSrc = connectedImage || (genieState.images as string[] | undefined)?.[0] || null

  const handleSelect = useCallback(() => {}, [])

  // Revoke previous blob URL when images change or on unmount
  const prevBlobUrlRef = useRef<string | null>(null)
  const currentImage = (genieState.images as string[] | undefined)?.[0] || null
  useEffect(() => {
    // Revoke old blob URL if it changed
    if (prevBlobUrlRef.current && prevBlobUrlRef.current !== currentImage) {
      URL.revokeObjectURL(prevBlobUrlRef.current)
    }
    prevBlobUrlRef.current = currentImage?.startsWith('blob:') ? currentImage : null
    return () => {
      if (prevBlobUrlRef.current) URL.revokeObjectURL(prevBlobUrlRef.current)
    }
  }, [currentImage])

  const handleFileUpload = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    onStateChange(prev => ({
      ...prev,
      images: [url],
      pendingFile: file,
    }))
  }, [onStateChange])

  const handleMaskExport = useCallback((maskDataUrl: string) => {
    onStateChange(prev => ({
      ...prev,
      maskData: maskDataUrl || undefined,
    }))
  }, [onStateChange])

  const handles: HandleMeta[] = nodeData.handles || MASK_HANDLES

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <MaskNode
        id={id}
        name={genieState.title as string || 'Mask Node'}
        selected={selected}
        imageSrc={imageSrc}
        nodeType="image"
        state={genieState as unknown as Record<string, unknown>}
        onStateChange={onStateChange}
        onSelect={handleSelect}
        onHoverChange={onHoverChange}
        onFileUpload={handleFileUpload}
        onMaskExport={handleMaskExport}
      />
    </>
  )
}

export const MaskNodeNode = React.memo(
  MaskNodeNodeComponent,
  (prev, next) => {
    const p = prev.data as unknown as MaskNodeNodeData
    const n = next.data as unknown as MaskNodeNodeData
    return (
      p.state.title === n.state.title &&
      p.state.images === n.state.images &&
      p.state.collapsed === n.state.collapsed &&
      p.state.maskData === n.state.maskData &&
      p.handles === n.handles &&
      prev.selected === next.selected &&
      prev.id === next.id
    )
  },
)
MaskNodeNode.displayName = 'MaskNodeNode'
